import React from 'react';

/** Fields returned by `POST /api/v1/doctors/patients/{id}/consent/request` (and aligned with GET consent summary). */
export type ConsentRequestAckDetails = {
  consent_request_count: number;
  consent_requests_remaining: number;
  max_consent_requests: number;
};

/** Raw POST body from FastAPI (snake_case). */
export type DoctorConsentRequestPostResponse = ConsentRequestAckDetails & {
  ok?: boolean;
  access_request_status?: string;
};

function pickNum(obj: Record<string, unknown>, ...keys: string[]): number | undefined {
  for (const k of keys) {
    const v = obj[k];
    if (typeof v === 'number' && !Number.isNaN(v)) return v;
  }
  return undefined;
}

/** Accept snake_case or camelCase from API / proxies. */
export function parseConsentRequestPostResponse(data: unknown): ConsentRequestAckDetails | null {
  if (!data || typeof data !== 'object') return null;
  const o = data as Record<string, unknown>;
  const count = pickNum(o, 'consent_request_count', 'consentRequestCount');
  const max = pickNum(o, 'max_consent_requests', 'maxConsentRequests');
  if (count === undefined || max === undefined) return null;
  const remaining = pickNum(o, 'consent_requests_remaining', 'consentRequestsRemaining') ?? 0;
  return {
    consent_request_count: count,
    consent_requests_remaining: remaining,
    max_consent_requests: max,
  };
}

type Props = {
  open: boolean;
  onClose: () => void;
  details: ConsentRequestAckDetails | null;
  /** When set (e.g. HTTP 400 max requests), show this instead of success copy. */
  limitReachedMessage?: string | null;
};

/** Success or limit-reached dialog after POST /consent/request. */
export const ConsentRequestAckModal: React.FC<Props> = ({ open, onClose, details, limitReachedMessage }) => {
  if (!open) return null;

  if (limitReachedMessage) {
    return (
      <div className="zivo-global-modal-overlay" onClick={onClose} role="presentation">
        <div
          className="zivo-global-modal-content"
          role="dialog"
          aria-modal="true"
          aria-labelledby="consent-limit-title"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="zivo-global-modal-header">
            <h3 id="consent-limit-title">Consent request limit</h3>
          </div>
          <div className="zivo-global-modal-body">
            <p>{limitReachedMessage}</p>
          </div>
          <div className="zivo-global-modal-footer" style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button
              type="button"
              className="zivo-global-modal-button zivo-global-modal-button--primary"
              onClick={onClose}
            >
              OK
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!details) return null;

  const { consent_request_count, consent_requests_remaining, max_consent_requests } = details;
  const times = consent_request_count === 1 ? 'time' : 'times';
  const reqWord = max_consent_requests === 1 ? 'request' : 'requests';

  return (
    <div className="zivo-global-modal-overlay" onClick={onClose} role="presentation">
      <div
        className="zivo-global-modal-content"
        role="dialog"
        aria-modal="true"
        aria-labelledby="consent-ack-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="zivo-global-modal-header">
          <h3 id="consent-ack-title">Consent request sent</h3>
        </div>
        <div className="zivo-global-modal-body">
          <p>
            Your consent request was sent. The patient will be notified. This has been sent {consent_request_count}{' '}
            {times}. {consent_requests_remaining} of {max_consent_requests} {reqWord} remaining before the limit is
            reached.
          </p>
        </div>
        <div className="zivo-global-modal-footer" style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button
            type="button"
            className="zivo-global-modal-button zivo-global-modal-button--primary"
            onClick={onClose}
          >
            OK
          </button>
        </div>
      </div>
    </div>
  );
};
